import { PlaybackAuthorizationRequestData } from './PlaybackAuthorizationRequest';
import { ContentAuthorizationData } from '../model/ContentAuthorizationData';
import { PlaybackAuthorizationService } from '../../../PlaybackAuthorizationService';
import { PlaybackAuthorizationData, Subtitle, SubtitleSidecarMetadata } from './PlaybackAuthorizationResponse';
import { createRequestBuilder, HttpHeaders, HttpRequest } from '@quickplay/foundation';
import { PlatformAuthorizer, AuthorizationUtil } from '@quickplay/platform-authorizer';
import { DrmScheme, MediaType, TrackKind } from '@quickplay/player-interface';
const CONTENT_TYPE_HEADER = 'Content-Type';
const CONTENT_TYPE_VALUE = 'application/json';
const X_DEVICE_ID_HEADER = 'X-Device-Id';


export function playbackAuthorizationServiceImpl(
  playbackAuthorizationEndPointUrl: string,
  platform: PlatformAuthorizer,
): PlaybackAuthorizationService {
  const playbackAuthorizationService: PlaybackAuthorizationService = {
    authorizePlayback(
      playbackAuthData: PlaybackAuthorizationRequestData,
      headers: HttpHeaders,
    ): Promise<ContentAuthorizationData> {
      const playbackAuthorizationRequestData: PlaybackAuthorizationRequestData = playbackAuthData;
      if (playbackAuthData.castDeviceId) playbackAuthorizationRequestData.castDeviceId = playbackAuthData.castDeviceId;
      return authorizePlayback(playbackAuthorizationRequestData, playbackAuthData.deviceToken, headers);
    },
  };

  const toTrackKind = (ty?: string): TrackKind | undefined => {
    if (!ty) return undefined;
    return ty === 'SUBTITLE' ? TrackKind.SUBTITLE : TrackKind.CAPTION;
  };

  const toSubtitle = (subtitles?: SubtitleSidecarMetadata[]): Subtitle[] | undefined =>
    subtitles?.map(({ ty, lang, su }) => ({ type: toTrackKind(ty), languageCode: lang, url: su }));

  async function authorizePlayback(
    playbackAuthorizationRequestData: PlaybackAuthorizationRequestData,
    deviceToken: string,
    headers?: HttpHeaders,
  ): Promise<ContentAuthorizationData> {
    try {
      const request = buildPlaybackAuthorizationRequest(playbackAuthorizationRequestData, deviceToken, headers);
      const result = await AuthorizationUtil.makeAuthorizedRequest<any, PlaybackAuthorizationData>(request, platform);

      return {
        contentId: result.data.contentId,
        contentUrl: result.data.contentUrl,
        drmLicenseUrl: result.data.licenseUrl,
        mediaType: result.data.mediaFormat as unknown as MediaType,
        drmScheme: result.data.drm as unknown as DrmScheme,
        licenseToken: result.data.licenseToken,
        skd: result.data.skd,
        subtitle: toSubtitle(result.data.subtitle),
        cuepoints: result.data.cuepoints,
        fpCertificateUrl: result.data.fpCertificateUrl,
        licenseProvider: result.data.licenseProvider,
        heartbeatFlag: result.data.heartbeatFlag,
        heartbeatToken: result.data.heartbeatToken,
        heartbeatFreq: result.data.heartbeatFreq,
        blackoutUrl: result.data.blackoutUrl,
        blackoutAction: result.data.blackoutAction,
        ssaiEnabled: result.data.ssaiEnabled,
        liveStartTime: result.data.liveStartTime,
        liveEndTime: result.data.liveEndTime,
        livePlaybackMode: result.data.livePlaybackMode,
        liveEventType: result.data.liveEventType,
        preSlateContentUrl: result.data.preSlateContentUrl,
        postSlateContentUrl: result.data.postSlateContentUrl,
        licenseRefreshToken: result.data.licenseRefreshToken,
        preSlateStartTime: result.data.preSlateStartTime,
        preSlateEndTime: result.data.preSlateEndTime,
        postSlateStartTime: result.data.postSlateStartTime,
        postSlateEndTime: result.data.postSlateEndTime,
        keyframe: result.data.keyframe,
        streamId: result.data.streamId,
        issuedAt: AuthorizationUtil.platformServiceTime(),
        bumperSegment: result.data.bumperSegment,
        adType: result.data.adType,
        csaiAdCuePointVmap: result.data.csaiAdCuePointVmap,
        csaiAdCuePointVast: result.data.csaiAdCuePointVast,
        live360Enabled: result.data.live360Enabled,
        live360StartTime: result.data.live360StartTime,
        live360EndTime: result.data.live360EndTime,
        live360ContentId: result.data.live360ContentId,
        assetKey: result.data.assetKey,
        cmsID: result.data.cmsID,
        apiKey: result.data.apiKey,
        pgmId: result.data.pgmId,
        pgmStartTime: result.data.pgmStartTime,
        pgmEndTime: result.data.pgmEndTime,
        pgmBlackout: result.data.pgmBlackout,
        custom: result.data.custom,
        ssaiAdProvider: result.data.ssaiAdProvider,
        csaiAdProvider: result.data.csaiAdProvider,
      };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  function buildPlaybackAuthorizationRequest(
    playbackAuthorizationRequestData: PlaybackAuthorizationRequestData,
    deviceToken: string,
    headers?: HttpHeaders,
  ): HttpRequest<PlaybackAuthorizationRequestData> {
    const defaultHeader: HttpHeaders = { [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE, [X_DEVICE_ID_HEADER]: deviceToken };
    return createRequestBuilder<PlaybackAuthorizationRequestData>()
      .url(playbackAuthorizationEndPointUrl)
      .headers(Object.assign(defaultHeader, headers))
      .path('/media/content/authorize')
      .post(playbackAuthorizationRequestData)
      .build();
  }

  return playbackAuthorizationService;
}
